import {
  CaretRight,
  ChatCircleDots,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetDepositsQuery } from '@entities/deposit';
import { useGetMeQuery } from '@entities/user';
import { DepositForm } from '@features/request-deposit';
import { Card } from '@shared/ui/Card';
import { SUPPORT_URL } from '@shared/config/support';
import { Button } from '@shared/ui/Button';
import { Screen } from '@shared/ui/Screen';
import { Spinner } from '@shared/ui/Spinner';
import styles from './WalletPage.module.css';

function submittedAt(value: string): string {
  const date = new Date(value);
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const sameDay = date.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay) return `Today at ${time}`;
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday at ${time}`;

  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`;
}

export function WalletPage() {
  const addMoney = useRef<HTMLElement>(null);
  const location = useLocation();
  const { data: user, isLoading: loadingUser } = useGetMeQuery();
  const {
    data: deposits,
    isLoading: loadingDeposits,
    isError: depositsError,
    refetch: refetchDeposits,
  } = useGetDepositsQuery();

  const pending = deposits?.deposits ?? [];

  useEffect(() => {
    if (!new URLSearchParams(location.search).has('source') || loadingDeposits) return;
    const timeout = window.setTimeout(
      () => addMoney.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      80,
    );
    return () => window.clearTimeout(timeout);
  }, [location.search, loadingDeposits]);

  return (
    <Screen title="Wallet">
      <Card className={styles.balanceCard}>
        <p className={styles.balanceLabel}>Available balance</p>
        {loadingUser ? (
          <div className={styles.balanceLoading}>
            <Spinner />
          </div>
        ) : (
          <p className={styles.balance}>{user?.balance.label ?? '—'}</p>
        )}
        <Button
          className={styles.addMoneyButton}
          onClick={() => addMoney.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          Add money
        </Button>
      </Card>

      {pending.length > 0 ? (
        <section className={styles.pendingSection} aria-labelledby="pending-deposits-title">
          <div className={styles.sectionHeading}>
            <div>
              <h2 id="pending-deposits-title" className={styles.sectionTitle}>
                Under review
              </h2>
              <p className={styles.sectionSubtitle}>We’ll message you after approval.</p>
            </div>
            <span className={styles.pendingCount}>{pending.length}</span>
          </div>

          <div className={styles.pendingList}>
            {pending.map((deposit) => (
              <Card key={deposit.id} className={styles.pendingRow}>
                <span className={styles.pendingIcon}>
                  <ClockCounterClockwise size={19} weight="bold" />
                </span>
                <div className={styles.pendingDetails}>
                  <span className={styles.pendingAmount}>
                    {deposit.amountPending ? 'Amount awaiting review' : deposit.amount.label}
                  </span>
                  <span className={styles.pendingDate}>{submittedAt(deposit.createdAt)}</span>
                </div>
                <span className={styles.pendingBadge}>Pending</span>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      <section ref={addMoney} className={styles.depositSection} aria-labelledby="add-money-title">
        <div className={styles.depositIntro}>
          <h2 id="add-money-title" className={styles.depositTitle}>
            Add money
          </h2>
        </div>

        {loadingDeposits ? (
          <Spinner />
        ) : depositsError ? (
          <Card className={styles.loadError}>
            <p className={styles.loadErrorTitle}>Couldn’t load payment details</p>
            <p className={styles.loadErrorBody}>Check your connection and try again.</p>
            <Button variant="secondary" onClick={() => void refetchDeposits()}>
              Try again
            </Button>
          </Card>
        ) : pending.length > 0 ? (
          <Card className={styles.loadError}>
            <p className={styles.loadErrorTitle}>Deposit already under review</p>
            <p className={styles.loadErrorBody}>
              Wait for an admin to approve or reject it before submitting another deposit.
            </p>
          </Card>
        ) : (
          <DepositForm
            methods={deposits?.paymentMethods ?? []}
          />
        )}
      </section>

      <a className={styles.support} href={SUPPORT_URL} target="_blank" rel="noreferrer">
        <span className={styles.supportIcon}>
          <ChatCircleDots size={20} weight="fill" />
        </span>
        <span className={styles.supportCopy}>
          <strong>Need help with a payment?</strong>
          <span>Chat with YeneShop support</span>
        </span>
        <CaretRight size={18} weight="bold" />
      </a>
    </Screen>
  );
}
